import { useState, useCallback } from "react";
import { useCurrentCompanyId } from "hooks/auth";
import { exportSupplierPdf, exportRatingPdf } from "../api/supplier-performance-api";
import { showToast } from "utils/helpers";

interface UseExportPdfReturn {
	exportSupplier: (supplierId: number, supplierName: string) => Promise<void>;
	exportRating: (ratingId: number) => Promise<void>;
	isExporting: boolean;
}

export const useExportPdf = (): UseExportPdfReturn => {
	const companyId = useCurrentCompanyId();
	const [isExporting, setIsExporting] = useState(false);

	const downloadBlob = (blob: Blob, filename: string) => {
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.style.display = "none";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const exportSupplier = useCallback(
		async (supplierId: number, supplierName: string) => {
			if (!companyId) {
				return;
			}
			setIsExporting(true);
			try {
				const blob = await exportSupplierPdf(companyId, supplierId);
				downloadBlob(blob, `${supplierName}-performance.pdf`);
				showToast("PDF exported successfully", "Success", true);
			} catch {
				showToast("Failed to export PDF. Please try again.", "Error");
			} finally {
				setIsExporting(false);
			}
		},
		[companyId]
	);

	const exportRating = useCallback(
		async (ratingId: number) => {
			if (!companyId) {
				return;
			}
			setIsExporting(true);
			try {
				const blob = await exportRatingPdf(ratingId);
				downloadBlob(blob, `rating-${ratingId}.pdf`);
				showToast("PDF exported successfully", "Success", true);
			} catch {
				showToast("Failed to export PDF. Please try again.", "Error");
			} finally {
				setIsExporting(false);
			}
		},
		[companyId]
	);

	return { exportSupplier, exportRating, isExporting };
};
